import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catch a render throw, so it costs a message instead of the whole document.
 *
 * There was no boundary at all. React unmounts the entire tree on an uncaught
 * render error, so ONE unguarded read anywhere — a response shape that changed, a
 * field missing from a 401 body — turned into a blank white screen with nothing on
 * it and no way to act. The owner reported exactly that on the phone, and the
 * server-side cause (evicted vault sessions, avengents-server 84e383a) explains
 * why a 401 arrived mid-render; it does not explain why a 401 mid-render should
 * end in an empty document. That part is ours.
 *
 * So this is not a fix for that bug. It is the reason the next one is legible:
 * whatever throws, the user gets a screen that says something happened and offers
 * the one action that always works.
 *
 * Deliberately plain — inline styles, no icons, no tokens. A boundary that depends
 * on the app's CSS having loaded is a boundary that renders blank in the case where
 * the CSS is what failed.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode
    /**
     * A COMPACT, in-place fallback for a boundary that guards one SECTION rather
     * than the document. Given it, a throw inside these children costs that
     * section, not the whole app — the machines list must never be able to
     * white-screen the terminal (owner, 2026-08-09: a `.map` in the settings
     * list took down everything). `reset` re-renders the children; the root
     * boundary keeps its fullscreen fallback by omitting this.
     */
    fallback?: (reset: () => void, err: Error) => ReactNode
  },
  { err: Error | null }
> {
  override state: { err: Error | null } = { err: null }

  static getDerivedStateFromError(err: Error) {
    return { err }
  }

  override componentDidCatch(err: Error, info: ErrorInfo) {
    // The stack is the only record — there is no error reporter here, and on a phone
    // the console is not reachable either, which is why the message is also shown.
    console.error('[boundary] render failed', err, info.componentStack)
  }

  reset = () => this.setState({ err: null })

  override render() {
    if (!this.state.err) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.reset, this.state.err)
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: 24,
          background: '#1e1e2e',
          color: '#cdd6f4',
          font: '15px/1.5 ui-sans-serif, system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <strong style={{ fontSize: 17 }}>Something broke while drawing this screen</strong>
        <p style={{ margin: 0, maxWidth: 420, color: '#a6adc8' }}>
          Reloading usually fixes it. If it keeps happening, the message below is the
          part worth reporting.
        </p>
        <code
          style={{
            maxWidth: '100%',
            padding: '8px 10px',
            borderRadius: 8,
            background: '#181825',
            color: '#f38ba8',
            font: '12px/1.4 ui-monospace, Menlo, monospace',
            overflowWrap: 'anywhere',
          }}
        >
          {this.state.err.message || String(this.state.err)}
        </code>
        <button
          type="button"
          onClick={() => location.reload()}
          style={{
            height: 40,
            padding: '0 20px',
            border: 'none',
            borderRadius: 10,
            background: '#a6e3a1',
            color: '#11111b',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}
